import React, { useEffect, useMemo, useRef, useState } from 'react';
import OlMap from 'ol/Map';
import Overlay from 'ol/Overlay';
import View from 'ol/View';
import Attribution from 'ol/control/Attribution';
import { fromLonLat } from 'ol/proj';
import { apply as applyMapStyle } from 'ol-mapbox-style';
import 'ol/ol.css';

import {
  useInstallationsQuery,
  useLatestSnapshotQuery,
  useInstallationFilesQuery,
  useCabinetHistoryQuery,
} from 'src/queries';
import { useSelectedInstallation } from 'src/Contexts/SelectedInstallationContext';
import { AuthenticatedImage } from 'src/Components/AuthenticatedImage';
import CabinetStatusBar from 'src/Components/ContentTemplate/Status/CabinetStatusBar';
import CabinetHistoryTable from './CabinetHistoryTable';
import type { Installation } from 'src/types';

import styles from './Dashboard.module.css';
import protectumLogo from 'src/assets/protectum_banner.jpg';

import { useTranslation } from 'react-i18next';

// OpenStreetMap vector tiles; free for commercial use, no API key.
const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';

// Shops, restaurants, fountains and the like are noise on a site map.
const HIDDEN_MAP_LAYERS = ['poi', 'water_name'];

const loadMapStyle = async (map: OlMap) => {
  const response = await fetch(MAP_STYLE_URL);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const style = (await response.json()) as { layers: { 'source-layer'?: string }[] };
  await applyMapStyle(map, {
    ...style,
    layers: style.layers.filter(
      (layer) => !HIDDEN_MAP_LAYERS.includes(layer['source-layer'] ?? '')
    ),
  });
};

interface MapProps {
  installation: Installation;
}

const Map: React.FC<MapProps> = ({ installation }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lat = installation.location?.latitude;
  const lon = installation.location?.longitude;

  useEffect(() => {
    if (!containerRef.current || lat == null || lon == null) return undefined;

    const center = fromLonLat([lon, lat]);
    const pin = document.createElement('div');
    pin.className = styles.mapPin;

    const map = new OlMap({
      target: containerRef.current,
      view: new View({ center, zoom: 17 }),
      // OSM's license requires the attribution to stay visible.
      controls: [new Attribution({ collapsible: false })],
      interactions: [],
      overlays: [new Overlay({ element: pin, position: center, positioning: 'bottom-center' })],
    });

    loadMapStyle(map).catch((error: unknown) => {
      console.error('Failed to load map style', error);
    });

    return () => {
      map.setTarget(undefined);
    };
  }, [lat, lon]);

  if (lat == null || lon == null) {
    return (
      <div className={styles.mapContainer}>
        <p>Map location not available for this installation.</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={styles.mapContainer}
      role="img"
      aria-label="Installation Location"
    />
  );
};

const Dashboard: React.FC = () => {
  const { data: installations = [] } = useInstallationsQuery();
  const { selectedInstallationID } = useSelectedInstallation();

  const { t, i18n } = useTranslation();

  // Derive installation details from installations array
  const installationDetails = useMemo(() => {
    return installations.find((inst) => inst.id === selectedInstallationID) || null;
  }, [installations, selectedInstallationID]);

  // Fetch the latest snapshot for the selected installation. We only need its
  // per-cabinet freshness (cabinet_status) and its own timestamp — not a full
  // history. Shares the query cache with the Status page (same hook + key).
  const { data: latestSnapshot, isPending: snapshotsPending } = useLatestSnapshotQuery(
    selectedInstallationID || null,
    null
  );

  // Fetch site images for the selected installation (v2 file system)
  const { data: siteImages = [] } = useInstallationFilesQuery(selectedInstallationID || '');

  // Which cabinet's history table is open, if any. Collapsed by default, so the
  // history request is only made once a user actually asks for it. The state is
  // tagged with the installation it belongs to so switching installations
  // collapses the table during render — an open cabinet number means nothing in
  // the newly-selected installation.
  const [expanded, setExpanded] = useState<{ installationId: string; nr: string } | null>(null);
  const expandedCabinet =
    expanded && expanded.installationId === selectedInstallationID ? expanded.nr : null;
  const setExpandedCabinet = (nr: string | null) =>
    setExpanded(
      nr === null || !selectedInstallationID ? null : { installationId: selectedInstallationID, nr }
    );

  const {
    data: cabinetHistory,
    isPending: historyPending,
    isError: historyError,
  } = useCabinetHistoryQuery(selectedInstallationID || null, 5, expandedCabinet !== null);

  if (!installationDetails) {
    return <div>Loading...</div>;
  }

  // Safe access to optional fields with fallbacks
  const dateTakenIntoOperation = installationDetails.installation_metadata?.date_start_operation
    ? new Date(installationDetails.installation_metadata.date_start_operation)
    : null;

  // Per-cabinet freshness from the latest snapshot. Guard on installation_id so
  // we never render a stale frame from a previously-selected installation (the
  // query keeps previous data while switching).
  const cabinetStatus =
    latestSnapshot && latestSnapshot.installation_id === selectedInstallationID
      ? latestSnapshot.cabinet_status
      : null;
  // Resolve the deadman window the same way the Status page does.
  const deadmanThresholdHours =
    installationDetails.configuration?.deadman_threshold ||
    installationDetails.deadman_threshold ||
    24;
  // Generic label for the "" pseudo-cabinet of a single-cabinet installation —
  // it gets the same chip as a numbered cabinet, just labeled "Installation" /
  // "Anlage" (the Status page's wording).
  const singleCabinetLabel = t('status.cabinet.installation');
  // Fallback for pre-feature snapshots, which have no cabinet_status at all:
  // synthesize a "" entry from the installation's own frame timestamp so the
  // chip still renders instead of dropping back to a bare line of text.
  const chipStatus =
    cabinetStatus && Object.keys(cabinetStatus).length > 0
      ? cabinetStatus
      : latestSnapshot && latestSnapshot.installation_id === selectedInstallationID
        ? { '': { last_measurement: latestSnapshot.measurement_timestamp } }
        : null;
  const hasAnyCabinet = !!chipStatus;
  const expandedLabel =
    expandedCabinet === '' ? singleCabinetLabel : t('status.cabinet', { nr: expandedCabinet });

  return (
    <div className={styles.dashboard}>
      <div className={styles.content}>
        <div className={styles.left}>
          <Map installation={installationDetails} />
          <div className={styles.infoBoxes}>
            <h2>{t('Latest Measurements:')}</h2>
            <hr />
            {snapshotsPending ? (
              <p>{t('Loading...')}</p>
            ) : hasAnyCabinet ? (
              // One chip per cabinet — single-cabinet installations get the same
              // chip under a generic label. Clicking a chip expands that
              // cabinet's last measurement times below.
              <div className={styles.measurementsBody}>
                <CabinetStatusBar
                  cabinetStatus={chipStatus}
                  deadmanThresholdHours={deadmanThresholdHours}
                  singleCabinetLabel={singleCabinetLabel}
                  expandedCabinet={expandedCabinet}
                  onCabinetClick={(nr) => setExpandedCabinet(expandedCabinet === nr ? null : nr)}
                />
                {expandedCabinet !== null && (
                  <CabinetHistoryTable
                    cabinetNr={expandedCabinet}
                    cabinetLabel={expandedLabel}
                    entries={cabinetHistory}
                    isPending={historyPending}
                    isError={historyError}
                  />
                )}
              </div>
            ) : (
              <p>{t('No measurements available')}</p>
            )}
            <hr />
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.imageContainer}>
            {siteImages.length > 0 ? (
              <AuthenticatedImage
                fileId={siteImages[0].id}
                alt="Installation view"
                className={styles.image}
              />
            ) : (
              <img className={styles.image} src={protectumLogo} alt="Installation view" />
            )}
          </div>
          <div className={styles.infoBoxes}>
            <h2>{t('Project Data:')}</h2>
            <hr />
            <p>
              {t('Name')}: {installationDetails.name}
              <br />
              {t('Address')}: {installationDetails.location?.address || 'N/A'}
              <br />
              <br />
              {t('Taken into operation')}:{' '}
              {dateTakenIntoOperation
                ? dateTakenIntoOperation.toLocaleDateString(i18n.language, {
                    weekday: 'long',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : 'N/A'}
              <br />
              {t('Monitored roof area')}:{' '}
              {installationDetails.installation_metadata?.monitored_area || 'N/A'} m<sup>2</sup>
              <br />
              {t('Type')}: {installationDetails.installation_metadata?.measuring_system || 'N/A'}
            </p>
            <hr />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
